/**
 * Static copy and input config for the Black Friday backwards planner.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Milestone content and date logic
 * live in ./plan.ts.
 */

export const STRINGS = {
  toolTitle: "Your dates",
  dateLabel: "Target Black Friday date",
  leadLabel: "Inventory lead time",
  leadHint:
    "How long between placing an order with your supplier and stock arriving.",
  icsButton: "Add these dates to my calendar",
  resultPrompt: "Pick your sale date to see your plan.",
  disclaimer:
    "Estimates only. Dates assume stock lands two weeks before your sale, a deposit on order with the balance due at shipping, and milestone timings based on common BFCM planning guides. Adjust for your own suppliers, payment terms and team.",
} as const;

/** Inventory lead-time presets, in days. */
export const LEAD_OPTIONS: { label: string; days: number }[] = [
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
  { label: "2 months", days: 61 },
  { label: "3 months", days: 91 },
];

export const DEFAULT_LEAD_DAYS = 30;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Backwards planner FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a Black Friday backwards planner?",
    answer:
      'A backwards planner works in the opposite direction to a normal to-do list. Instead of starting today and listing tasks, you start from your sale date and count backwards, so every milestone (placing your stock order, briefing creative, freezing your site) gets a real calendar date rather than a vague "do this soon."',
  },
  {
    question: "How far in advance should you order stock for Black Friday?",
    answer:
      "It depends entirely on your supplier's lead time, which is why this tool asks for it directly rather than giving one generic answer. As a rule of thumb, brands ordering internationally or from manufacturers often need several months of runway, while domestic suppliers with stock on hand can turn around in weeks. Enter your own lead time above and the order deadline updates automatically.",
  },
  {
    question: "What is supplier lead time and why does it matter here?",
    answer:
      "Supplier lead time is the gap between placing an order and the stock physically arriving. It matters because it is the constraint the whole plan hinges on: this tool assumes stock needs to land about two weeks before your sale for counting and syncing, then works backwards by your lead time to find the exact date you need to place the order.",
  },
  {
    question: "How does the planner work out my other deadlines?",
    answer:
      "Every milestone (locking your offer, briefing creative, cleaning your email list, starting ad warm-up, code freeze) is set a fixed number of weeks before your sale date, based on common BFCM planning guides. The stock order deadline is the exception: it is calculated from your lead time, not a fixed offset, since that is the one date most brands get wrong.",
  },
  {
    question: "What if a deadline has already passed?",
    answer:
      "The plan still shows it, marked **Passed**, so you can see exactly what is now running late rather than having it disappear. If your stock order deadline has passed, the tool suggests asking your supplier about faster options or planning the sale around stock you can realistically get in time.",
  },
  {
    question: "Can I add these dates to my calendar?",
    answer:
      "Yes. The **Add these dates to my calendar** button downloads an .ics file with one all-day event per milestone, which you can import into Google Calendar, Outlook or Apple Calendar to share the plan with your team.",
  },
  {
    question: "Where can I get help with the rest of my Black Friday prep?",
    answer:
      "Once your dates are set, the [readiness scorecard](/tools/readiness-scorecard) checks what is actually in place across offer, inventory, site, email, ads and fulfilment, and the [full Black Friday toolkit](/tools) has calculators for margin, stock quantity and financing.",
  },
];
